<template>
  <div
      v-if="showPhoneLogin"
      class="fixed left-0 right-0 top-0 z-20 flex h-11 items-center justify-end md:hidden"
      style="padding-top: env(safe-area-inset-top, 0px); box-sizing: content-box; padding-left: max(0.75rem, env(safe-area-inset-left, 0px)); padding-right: max(0.75rem, env(safe-area-inset-right, 0px));"
  >
    <button
        class="inline-flex min-h-11 min-w-11 items-center gap-1.5 rounded-full px-3 text-sm font-semibold text-indigo-700 transition-colors hover:bg-indigo-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/60 active:scale-[0.96] dark:text-indigo-300 dark:hover:bg-indigo-950/40"
        type="button"
        @click="$emit('show-login')"
    >
      <LogInIcon class="h-4 w-4" aria-hidden="true"/>
      {{ $t('auth.login') }}
    </button>
  </div>
  <header
      v-if="!isNativePlatform"
      class="fixed left-0 right-0 top-0 z-20 hidden w-full items-center justify-between gap-3 border-b border-gray-200/90 bg-white/90 px-4 py-2 shadow-sm backdrop-blur-md dark:border-gray-700/80 dark:bg-gray-950/90 dark:shadow-black/20 md:flex"
      style="padding-top: calc(env(safe-area-inset-top, 0px) + 0.5rem); padding-left: max(1rem, env(safe-area-inset-left, 0px)); padding-right: max(1rem, env(safe-area-inset-right, 0px));"
  >
    <router-link
        class="group flex min-w-0 max-w-[65%] items-center gap-2.5 rounded-xl py-1.5 pl-0.5 pr-2 transition-colors hover:bg-gray-100/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/50 focus-visible:ring-offset-2 dark:hover:bg-gray-800/80 dark:focus-visible:ring-offset-gray-950 sm:max-w-none"
        to="/"
    >
      <img
          alt=""
          class="h-8 w-8 shrink-0 rounded-lg object-cover ring-1 ring-black/[0.06] dark:ring-white/[0.08]"
          src="/yumnia-Logo-48.png"
      />
      <span class="truncate text-lg font-semibold tracking-tight text-gray-900 dark:text-gray-50 rtl:rtl">
        {{ $t('brand') }}
      </span>
    </router-link>

    <nav class="hidden min-w-0 flex-1 items-center gap-1 md:flex md:ps-6">
      <router-link
          v-for="tab in desktopTabs"
          :key="tab.name"
          :to="tab.to"
          :class="isActive(tab)
            ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400'
            : 'text-gray-600 hover:bg-gray-100/90 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-800/80 dark:hover:text-gray-50'"
          class="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/50"
      >
        <component :is="tab.icon" class="h-4 w-4" aria-hidden="true" />
        {{ $t(tab.label) }}
      </router-link>
    </nav>

    <div class="flex shrink-0 items-center">
      <template v-if="auth.user">
        <button
            class="group hidden max-w-[11rem] cursor-pointer items-center gap-2 rounded-full py-1 pl-1 pr-2 transition-colors hover:bg-gray-100/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/50 focus-visible:ring-offset-2 md:flex dark:hover:bg-gray-800/80 dark:focus-visible:ring-offset-gray-950 sm:max-w-[14rem] sm:pr-3 rtl:space-x-reverse"
            type="button"
            @click="goToProfile"
        >
          <div class="relative inline-flex shrink-0 items-center justify-center rounded-full ring-2 ring-transparent transition group-hover:ring-gray-200/80 dark:group-hover:ring-gray-600/80">
            <template v-if="auth.user?.is_premium">
              <div class="premium-avatar-ring">
                <div class="premium-avatar-ring__inner">
                  <img
                      :alt="$t('auth.avatar')"
                      :src="auth.user.avatar ? auth.user.avatar : '/avatar_placeholder.png'"
                      class="h-8 w-8 rounded-full object-cover"
                      @error="handleAvatarError"
                  />
                </div>
              </div>
            </template>
            <img
                v-else
                :alt="$t('auth.avatar')"
                :src="auth.user.avatar ? auth.user.avatar : '/avatar_placeholder.png'"
                class="h-8 w-8 rounded-full object-cover ring-1 ring-black/[0.06] dark:ring-white/10"
                @error="handleAvatarError"
            />
          </div>
          <span class="hidden min-w-0 truncate text-sm font-medium text-gray-700 dark:text-gray-200 sm:inline">
            {{ auth.user.name }}
          </span>
        </button>
      </template>
      <template v-else>
        <button
            v-if="!isFunnelRoute"
            class="inline-flex items-center justify-center gap-1.5 rounded-full bg-gradient-to-r from-indigo-600 to-violet-600 px-4 py-2.5 text-sm font-semibold text-white shadow-md shadow-indigo-500/25 transition hover:from-indigo-500 hover:to-violet-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 active:scale-[0.98] dark:focus-visible:ring-offset-gray-950"
            type="button"
            @click="$emit('show-login')"
        >
          <LogInIcon class="h-4 w-4 opacity-95" aria-hidden="true"/>
          {{ $t('auth.login') }}
        </button>
        <button
            v-else
            class="text-sm font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition px-2 py-1"
            type="button"
            @click="$emit('show-login')"
        >
          {{ $t('auth.login') }}
        </button>
      </template>
    </div>
  </header>
</template>

<script setup>
import { LogIn as LogInIcon } from 'lucide-vue-next'
import { computed, watchEffect, onBeforeUnmount } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { useRouter, useRoute } from 'vue-router'
import { handleAvatarError } from '@/utils/imageHelpers'
import { usePrimaryNav } from '@/composables/usePrimaryNav.js'

const auth = useAuthStore()
const router = useRouter()
const route = useRoute()

const { desktopTabs: primaryDesktopTabs, isActive } = usePrimaryNav()

// The avatar on the right is the account/Profile entry on desktop, so the
// Profile tab is dropped from the inline link row to avoid duplication.
const desktopTabs = computed(() => primaryDesktopTabs.value.filter((tab) => tab.name !== 'Profile'))

const isFunnelRoute = computed(() => !!route.meta.hideLoginButton)
const isNativePlatform = !!window.Capacitor?.isNativePlatform?.()

defineEmits(['show-login'])

const showPhoneLogin = computed(() => !auth.user)

watchEffect(() => {
  document.documentElement.classList.toggle('phone-login-strip', showPhoneLogin.value)
})

onBeforeUnmount(() => document.documentElement.classList.remove('phone-login-strip'))

function goToProfile() {
  router.push({ name: 'Profile' })
}
</script>
